import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios from '@axios';
import { RootState } from '@redux/reducers';
import { RestrictedItemResponse, RetrictedItemRequest } from '@models/restricted-item';
import { Paginated } from '@models/paginated';
import '@extensions/string';
import { ThunkCallback } from '../model/thunk-callback';
import { addNotification } from '../notification';
import { translate } from '@components/i18n';
import { PaginatedResponseApi } from '@models/order/items/response';

export const fetchRestrictedItems = createAsyncThunk<
    PaginatedResponseApi<RestrictedItemResponse>
>('restricted-items/list', async (_, thunkAPI) => {
    try {
        const response = await axios.get<PaginatedResponseApi<RestrictedItemResponse>>(
            `/produtosaprovacao`,
        );

        const { data, status } = response;

        if (!data) {
            return {
                content: [],
                number: 0,
                totalElements: 0,
                size: 0,
                totalPages: 0,
                numberOfElements: 0,
                pageable: null,
                last: true,
                first: true,
                sort: null,
                empty: true,
            };
        } else {
            if (status === 200) {
                return data;
            }
            throw new Error();
        }
    } catch (e) {
        // TODO: here will be fetch's actions
        return thunkAPI.rejectWithValue(translate('general.erroListingItems'));
    }
});

export const createRestrictedItem = createAsyncThunk<
    RestrictedItemResponse,
    ThunkCallback<RetrictedItemRequest>
>('restricted-items/create', async (request, thunkAPI) => {
    try {
        const response = await axios.post<RestrictedItemResponse>('/produtosaprovacao', request.data);

        const { data, status } = response;

        if (status === 201 && data) {
            thunkAPI.dispatch(
                addNotification({
                    type: 'success',
                    message: translate('general.updated'),
                    title: translate('general.success'),
                    notificationKey: request.notificationKey,
                    callback: request.onSuccess,
                }),
            );

            return data;
        } else {
            return thunkAPI.rejectWithValue(data);
        }
    } catch (e) {
      if(e.response.data && e.response.data.length > 0){
          e.response.data.forEach((error, index) => {
              thunkAPI.dispatch(
                  addNotification({
                      type: 'error',
                      message: error.msgUsuario,
                      title: translate('general.errorT'),
                      notificationKey: index === 0 ? request.notificationKey : null
                  }),
              );
          })
      }else{
          thunkAPI.dispatch(
              addNotification({
                  type: 'error',
                  message: translate('general.error'),
                  title: translate('general.errorT'),
                  notificationKey: request.notificationKey,
              }),
          );
      }
      return thunkAPI.rejectWithValue(e.message);
    }
});

export const removeRestrictedItem = createAsyncThunk<void, ThunkCallback<number>>(
    'restricted-items/remove',
    async (req, thunkAPI) => {
        try {
          thunkAPI.dispatch(
            addNotification({
              type: 'loading',
              message: translate('general.deleting'),
              title: translate('general.deleting'),
              notificationKey: req.notificationKey
           }))
          const response = await axios.delete('/produtosaprovacao/' + req.data);
          if (response.status === 204) {
            thunkAPI.dispatch(removeItemState(req.data));
          }
          thunkAPI.dispatch(addNotification({
              type: 'success',
              message: translate('general.success'),
              title: translate('general.deleteSuccess'),
              notificationKey: req.notificationKey,
              callback: req.onSuccess,
          }))
        } catch (e) {
          if(e.response.data && e.response.data.length > 0){
              e.response.data.forEach((error, index) => {
                  thunkAPI.dispatch(
                      addNotification({
                          type: 'error',
                          message: error.msgUsuario,
                          title: translate('general.errorT'),
                          notificationKey: index === 0 ? req.notificationKey : null
                      }),
                  );
              })
          }else{
              thunkAPI.dispatch(
                  addNotification({
                      type: 'error',
                      message: translate('general.error'),
                      title: translate('general.errorT'),
                      notificationKey: req.notificationKey,
                  }),
              );
          }
          return thunkAPI.rejectWithValue(e.message);
        }
    },
);

interface IUItemsState {
    response?: PaginatedResponseApi<RestrictedItemResponse>;
    isFetching: boolean;
    isSuccess: boolean;
    isError: boolean;
    errorMessage?: string;
}

const initialState: IUItemsState = {
    isFetching: false,
    isSuccess: false,
    isError: false,
    errorMessage: '',
};

const restrictedItemsSlice = createSlice({
    name: 'restrictedItemsSlice',
    initialState,
    reducers: {
        clearState: (state) => {
            state = { ...initialState };
            return state;
        },
        removeItemState: (state, action: PayloadAction<number>) => {
            state.response.content = state.response.content.filter((u) => u.id !== action.payload);
            return state;
        },
    },
    extraReducers: {
        [fetchRestrictedItems.pending.toString()]: (state) => {
            state.isFetching = true;
            return state;
        },
        [fetchRestrictedItems.rejected.toString()]: (state, action) => {
            state.isFetching = false;
            state.isError = true;
            state.errorMessage = action.payload;
            return state;
        },
        [fetchRestrictedItems.fulfilled.toString()]: (
            state,
            { payload }: PayloadAction<PaginatedResponseApi<RestrictedItemResponse>>,
        ) => {
            state.isFetching = false;
            state.isSuccess = true;
            state.response = payload;
            return state;
        },
    },
});

export const restrictedItemSelector = (state: RootState): IUItemsState => state.restrictedItems;

export const allRestrictedItems = ({ restrictedItems }: RootState): RestrictedItemResponse[] => restrictedItems.response?.content || [];

export const { clearState, removeItemState } = restrictedItemsSlice.actions;

export default restrictedItemsSlice.reducer;
